import { styled } from "@mui/material/styles";
import { AppBar, Box, useMediaQuery, useTheme } from "@mui/material";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button } from "./Button";
import { NavLink } from "./NavLink";
import { useAppUrl } from "../hooks/useAppUrl";

const StyledAppBar = styled(AppBar)(() => ({
  background: "#ffffff",
  boxShadow: "0px 0px 20px 0px rgba(0, 0, 0, 0.04)",
  position: "fixed",
  top: 0,
  left: 0,
  right: 0,
  zIndex: 1000,
  padding: "0 12px",
}));

const HeaderContainer = styled(Box)(() => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  width: "100%",
  maxWidth: "1200px",
  margin: "0 auto",
  padding: "16px 0",
}));

const LogoWrapper = styled(Box)(() => ({
  display: "flex",
  alignItems: "center",

  "& a": {
    lineHeight: 0,
    textDecoration: "none",
  },

  "& img": {
    width: "195px",
    height: "26px",
  },
}));

const Navigation = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: 0,

  [theme.breakpoints.down("lg")]: {
    display: "none",
  },
}));

const ButtonsContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: "8px",

  [theme.breakpoints.down("lg")]: {
    "& .desktop-buttons": {
      display: "none",
    },
  },
}));

const MobileMenuButtonWrapper = styled(Box)(({ theme }) => ({
  display: "none",

  [theme.breakpoints.down("lg")]: {
    display: "flex",
  },
}));

const DesktopButtonsContainer = styled(Box)(() => ({
  display: "flex",
  gap: "8px",
}));

interface HeaderProps {
  onMenuClick?: () => void;
}

export const Header = ({ onMenuClick }: HeaderProps) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("lg"));
  const location = useLocation();
  const { t } = useTranslation();
  const appUrl = useAppUrl();

  const isActive = (path: string) => location.pathname === path;

  return (
    <StyledAppBar>
      <HeaderContainer>
        <LogoWrapper>
          <Link to="/">
            <img src="/assets/logo.svg" alt="Selfmade ID" />
          </Link>
        </LogoWrapper>

        <Navigation>
          <NavLink to="/individuals" active={isActive("/individuals")}>
            {t("nav.individuals")}
          </NavLink>
          <NavLink to="/business" active={isActive("/business")}>
            {t("nav.business")}
          </NavLink>
          <NavLink to="/contacts" active={isActive("/contacts")}>
            {t("nav.contacts")}
          </NavLink>
        </Navigation>

        <ButtonsContainer>
          {isMobile ? (
            <MobileMenuButtonWrapper>
              <Button variant="white" onClick={onMenuClick}>
                {t("nav.menu")}
              </Button>
            </MobileMenuButtonWrapper>
          ) : (
            <DesktopButtonsContainer className="desktop-buttons">
              <Button 
                variant="white" 
                onClick={() => window.open(appUrl, '_blank', 'noopener,noreferrer')}
              >
                {t("nav.signIn")}
              </Button>
              <Button 
                variant="primary"
                onClick={() => window.open(appUrl, '_blank', 'noopener,noreferrer')}
              >
                {t("nav.demo")}
              </Button>
            </DesktopButtonsContainer>
          )}
        </ButtonsContainer>
      </HeaderContainer>
    </StyledAppBar>
  );
};
